/**
 * Mutation handlers for the dashboard workspace tab. Every mutation routes
 * through the S1 store → main-process repository (the sole writer; licensing
 * enforced there). Full-document saves REPLAY on conflict (C1): the store
 * reloads the mirror, the intent re-applies to the fresh revision, bounded
 * retries — a concurrent MCP mutation must not silently swallow a user's
 * drag or rename.
 */
import { useCallback } from 'react';
import { toast } from '@/components/ui/toaster';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useDashboardStore } from '../store/dashboardStore';
import { isQueryBackedTile } from '../types/dashboardTypes';
import type {
  Dashboard,
  DashboardParameter,
  DashboardTile,
  StatTile,
  TileAlertRule,
  TileLayout,
} from '../types/dashboardTypes';
import type { AddTileSubmitMeta } from '../components/add-tile/AddTileDialog';
import { refetchTileQueries, runTileManually } from '../services/manualTileRun';

const nextFreeY = (tiles: DashboardTile[]): number =>
  tiles.reduce((max, t) => Math.max(max, t.layout.y + t.layout.h), 0);

const MAX_SAVE_REPLAYS = 3;

export function useDashboardActions(tabId: string, dashboard: Dashboard | null) {
  const { track } = useAnalytics();
  const updateTabContent = useWorkspaceStore((s) => s.updateTabContent);
  const store = useDashboardStore;

  const showTo = useCallback(
    (id: string) => updateTabContent<'dashboard'>(tabId, { dashboardId: id }),
    [tabId, updateTabContent],
  );

  /**
   * Full-document save as a rebasable intent: on `conflict` the store has
   * already reloaded the mirror, so re-apply the transform to the fresh
   * dashboard and retry (bounded). Surfaces failures — never silent.
   */
  const saveWithReplay = useCallback(
    async (what: string, apply: (d: Dashboard) => Dashboard) => {
      if (!dashboard) return;
      let target = dashboard;
      for (let attempt = 0; attempt < MAX_SAVE_REPLAYS; attempt++) {
        const result = await store.getState().save(apply(target), target.revision);
        if (result.ok) return;
        if (result.error !== 'conflict') {
          toast.error({ title: `Could not save ${what}`, description: result.detail });
          return;
        }
        const fresh = store.getState().dashboards.find((d) => d.id === target.id);
        if (!fresh) return; // deleted concurrently — nothing to replay onto
        target = fresh;
      }
      toast.error({
        title: `Could not save ${what}`,
        description: 'The dashboard kept changing while saving. Try again.',
      });
    },
    [dashboard, store],
  );

  const createDashboard = useCallback(
    async (name: string) => {
      const result = await store.getState().create(name);
      if (result.ok) {
        showTo(result.value.id);
        track('feature:dashboard_create');
      } else if (result.error === 'free-limit') {
        // Defense-in-depth (repo rule: main enforces, the button is only a
        // UX nicety): a stale renderer could still let this click through.
        toast.error({ title: 'Free plan limit reached', description: result.detail });
      } else if (result.error === 'io') {
        toast.error({ title: 'Could not create dashboard', description: result.detail });
      }
    },
    [store, showTo, track],
  );

  const duplicateDashboard = useCallback(async () => {
    if (!dashboard) return;
    const result = await store.getState().duplicate(dashboard.id);
    if (result.ok) {
      showTo(result.value.id);
      track('feature:dashboard_duplicate');
    } else if (result.error === 'free-limit') {
      // Also surfaces via the store's typed freeLimit state (banner) — both
      // fire, matching the Object Explorer's duplicate action.
      toast.error({ title: 'Free plan limit reached', description: result.detail });
    } else if (result.error === 'io') {
      toast.error({ title: 'Could not duplicate dashboard', description: result.detail });
    }
  }, [dashboard, store, showTo, track]);

  const renameDashboard = useCallback(
    (name: string) => saveWithReplay('the new name', (d) => ({ ...d, name })),
    [saveWithReplay],
  );

  /** S10: parameter declarations live on the definition (values do not). */
  const saveParameters = useCallback(
    (parameters: DashboardParameter[]) =>
      saveWithReplay('the parameters', (d) => ({ ...d, parameters })),
    [saveWithReplay],
  );

  /** S13: Present Mode playlist config — `undefined` disables the playlist. */
  const savePlaylist = useCallback(
    (playlist: Dashboard['playlist']) => saveWithReplay('the playlist', (d) => ({ ...d, playlist })),
    [saveWithReplay],
  );

  const deleteDashboard = useCallback(async () => {
    if (!dashboard) return;
    const result = await store.getState().remove(dashboard.id);
    if (result.ok) {
      // Clearing the pointer lets the tab's resolve effect pick the
      // most-recently-updated dashboard — same rule as tab open.
      updateTabContent<'dashboard'>(tabId, { dashboardId: null });
      track('feature:dashboard_delete');
    } else {
      toast.error({ title: 'Could not delete dashboard', description: result.detail });
    }
  }, [dashboard, store, tabId, updateTabContent, track]);

  /** Dialog submit (S6): add or update, analytics. A data-input change
   * (SQL/connection/database/result set) needs no refetch call: those
   * inputs are in the tile's cache key, so the edit re-keys and fetches. */
  const submitTile = useCallback(
    async (tile: DashboardTile, meta: AddTileSubmitMeta): Promise<boolean> => {
      if (!dashboard) return false;
      const result = meta.isEdit
        ? await store.getState().updateTile(dashboard.id, tile)
        : await store.getState().addTile(dashboard.id, tile);
      if (!result.ok) {
        toast.error({ title: 'Could not save tile', description: result.detail });
        return false;
      }
      if (meta.isEdit) {
        track('feature:dashboard_tile_update', { tileType: meta.tileType });
      } else {
        track('feature:dashboard_tile_add', {
          tileType: meta.tileType,
          source: meta.source,
          autopilot: meta.autopilot,
          ...(meta.templateCategory ? { template_category: meta.templateCategory } : {}),
        });
      }
      return true;
    },
    [dashboard, store, track],
  );

  /** Kebab "Refresh now" — manual trigger, destructive-confirm capable. */
  const refreshTile = useCallback(
    async (tile: DashboardTile) => {
      if (!dashboard || !isQueryBackedTile(tile)) return;
      const result = await runTileManually(dashboard.id, tile);
      if (result.ok) track('feature:dashboard_tile_refresh', { tileType: tile.tileType });
    },
    [dashboard, track],
  );

  /**
   * D2 — grant persisted schedule trust for a write-classified interval tile
   * (the confirmation the plan requires). Main hashes the persisted SQL so the
   * trust auto-revokes if the query is edited. After granting, refetch under
   * the interval policy so the tile leaves its "scheduled refresh blocked"
   * state and its next scheduler tick is allowed too.
   */
  const grantScheduleTrust = useCallback(
    async (tile: DashboardTile) => {
      if (!dashboard || !isQueryBackedTile(tile)) return;
      const result = await window.electronAPI?.dashboards?.grantScheduleTrust(dashboard.id, tile.id);
      if (result && !result.ok) {
        toast.error({ title: 'Could not allow scheduled refresh', description: result.detail });
        return;
      }
      track('feature:dashboard_schedule_trust', { tileType: tile.tileType });
      refetchTileQueries(dashboard.id, tile.id);
    },
    [dashboard, track],
  );

  const duplicateTile = useCallback(
    async (tile: DashboardTile) => {
      if (!dashboard) return;
      const copy: DashboardTile = {
        ...tile,
        id: crypto.randomUUID(),
        title: `${tile.title} copy`,
        layout: { ...tile.layout, x: 0, y: nextFreeY(dashboard.tiles) },
      };
      const result = await store.getState().addTile(dashboard.id, copy);
      if (result.ok) track('feature:dashboard_tile_duplicate', { tileType: tile.tileType });
      else toast.error({ title: 'Could not duplicate tile', description: result.detail });
    },
    [dashboard, store, track],
  );

  const removeTile = useCallback(
    async (tileId: string) => {
      if (!dashboard) return;
      const tile = dashboard.tiles.find((t) => t.id === tileId);
      const result = await store.getState().removeTile(dashboard.id, tileId);
      if (result.ok) track('feature:dashboard_tile_remove', { tileType: tile?.tileType ?? 'unknown' });
    },
    [dashboard, store, track],
  );

  const commitLayout = useCallback(
    (layouts: Record<string, TileLayout>) =>
      // Replayable by construction: layouts key by tile id, so tiles added or
      // removed concurrently keep their own layout / drop out naturally.
      saveWithReplay('the layout', (d) => ({
        ...d,
        tiles: d.tiles.map((t) => (layouts[t.id] ? { ...t, layout: layouts[t.id] } : t)),
      })),
    [saveWithReplay],
  );

  /** AlertRuleDialog save/remove (S12) — updates ONLY the `alertRule` field. */
  const updateAlertRule = useCallback(
    async (tile: StatTile, alertRule: TileAlertRule | undefined) => {
      if (!dashboard) return false;
      const result = await store.getState().updateTile(dashboard.id, { ...tile, alertRule });
      if (!result.ok) {
        toast.error({ title: 'Could not save alert rule', description: result.detail });
        return false;
      }
      track('feature:dashboard_alert_rule_save', {
        enabled: alertRule !== undefined,
        background: alertRule?.background ?? false,
      });
      return true;
    },
    [dashboard, store, track],
  );

  /**
   * Toolbar bulk-refresh override (S12) — the prototype's dashboard-level
   * "every 30s" control, reinterpreted as an explicit bulk action: sets
   * `refresh.mode` on every query-backed tile after the caller's confirm
   * (note tiles have no `refresh` field and are skipped).
   */
  const bulkOverrideRefresh = useCallback(
    (seconds: number | null) =>
      saveWithReplay('the refresh setting', (d) => ({
        ...d,
        tiles: d.tiles.map((t) =>
          isQueryBackedTile(t)
            ? { ...t, refresh: seconds === null ? { mode: 'manual' as const } : { mode: 'interval' as const, seconds } }
            : t,
        ),
      })).then(() => track('feature:dashboard_bulk_refresh_override', { seconds: seconds ?? 0 })),
    [saveWithReplay, track],
  );

  return {
    showTo,
    createDashboard,
    duplicateDashboard,
    renameDashboard,
    saveParameters,
    savePlaylist,
    deleteDashboard,
    submitTile,
    refreshTile,
    grantScheduleTrust,
    duplicateTile,
    removeTile,
    commitLayout,
    updateAlertRule,
    bulkOverrideRefresh,
  };
}
